import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../services/api';
import EditQRModal from '../components/EditQRModal';

const periods = [
  { key: '5min', label: '5 min' }, { key: '1hr', label: '1 hour' }, { key: '5hr', label: '5 hours' },
  { key: '24hr', label: '24 hours' }, { key: '7d', label: '7 days' }, { key: '30d', label: '30 days' }, { key: 'all', label: 'All time' }
];

export default function QRDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [qr, setQr] = useState(null);
  const [scanData, setScanData] = useState({ scans: [], chart: [], total: 0 });
  const [period, setPeriod] = useState('7d');
  const [svgUrl, setSvgUrl] = useState('');
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    api.get(`/qr/${id}`).then(r => setQr(r.data)).catch(() => navigate('/'));
    const token = localStorage.getItem('token');
    setSvgUrl(`${process.env.REACT_APP_API_URL || 'http://localhost:4000/api'}/qr/${id}/image?token=${token}`);
  }, [id]);

  useEffect(() => {
    api.get(`/scans/${id}`, { params: { period } }).then(r => setScanData(r.data));
  }, [id, period]);

  const loadImage = async () => {
    try {
      const res = await api.get(`/qr/${id}/image`, { responseType: 'blob' });
      setSvgUrl(URL.createObjectURL(res.data));
    } catch {}
  };
  useEffect(() => { loadImage(); }, [id]);

  if (!qr) return <p>Loading...</p>;

  const chartData = scanData.chart.map(d => ({
    time: new Date(d.time).toLocaleString(),
    count: d.count
  }));

  return (
    <div>
      <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer', marginBottom: 16, fontSize: 14 }}>← Back to Dashboard</button>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 24 }}>
        {/* Details */}
        <div>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,.06)', marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0 }}>{qr.title}</h2>
              <button onClick={() => setEditing(true)} style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>✏️ Edit</button>
            </div>
            <p style={{ color: '#666', marginTop: 4 }}>{qr.target_url}</p>
            <p style={{ marginTop: 8 }}>
              Code: <code style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: 4 }}>/q/{qr.code}</code>
              {qr.category_name && <span style={{ marginLeft: 12, background: '#e0e7ff', padding: '2px 10px', borderRadius: 12, fontSize: 12 }}>{qr.category_name}</span>}
              <span style={{ marginLeft: 12, color: qr.is_active ? '#22c55e' : '#ef4444', fontWeight: 600 }}>{qr.is_active ? 'Active' : 'Inactive'}</span>
            </p>
            <p style={{ marginTop: 8, fontSize: 28, fontWeight: 700, color: '#6366f1' }}>{qr.scan_count} total scans</p>
          </div>

          {/* Chart */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,.06)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3>Scan Analytics</h3>
              <div style={{ display: 'flex', gap: 4 }}>
                {periods.map(p => (
                  <button key={p.key} onClick={() => setPeriod(p.key)}
                    style={{ padding: '4px 10px', borderRadius: 6, border: 'none', fontSize: 12, cursor: 'pointer', fontWeight: period === p.key ? 700 : 400,
                      background: period === p.key ? '#6366f1' : '#f1f5f9', color: period === p.key ? '#fff' : '#666' }}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="time" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p style={{ textAlign: 'center', color: '#888', padding: 40 }}>No scans in this period</p>
            )}
            <p style={{ textAlign: 'center', marginTop: 8, color: '#888', fontSize: 13 }}>{scanData.total} scans in period</p>
          </div>

          {/* Recent scans table */}
          {scanData.scans.length > 0 && (
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,.06)', marginTop: 20 }}>
              <h3 style={{ marginBottom: 12 }}>Recent Scans</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                    <th style={{ textAlign: 'left', padding: 8 }}>Time</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>IP</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>User Agent</th>
                  </tr>
                </thead>
                <tbody>
                  {scanData.scans.slice(0, 20).map(s => (
                    <tr key={s.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                      <td style={{ padding: 8 }}>{new Date(s.scanned_at).toLocaleString()}</td>
                      <td style={{ padding: 8 }}>{s.ip_address}</td>
                      <td style={{ padding: 8, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.user_agent}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* QR Image sidebar */}
        <div>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,.06)', textAlign: 'center', position: 'sticky', top: 24 }}>
            {svgUrl && <img src={svgUrl} alt="QR Code" style={{ width: '100%', borderRadius: 8 }} />}
            <p style={{ marginTop: 12, fontSize: 12, color: '#888' }}>Scan or share this QR code</p>
            <p style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
              Colors: <span style={{ display: 'inline-block', width: 14, height: 14, background: qr.fg_color, borderRadius: 3, verticalAlign: 'middle', border: '1px solid #ddd' }} /> {qr.fg_color} / <span style={{ display: 'inline-block', width: 14, height: 14, background: qr.bg_color, borderRadius: 3, verticalAlign: 'middle', border: '1px solid #ddd' }} /> {qr.bg_color}
            </p>
          </div>
        </div>
      </div>

      {editing && (
        <EditQRModal
          qr={qr}
          onClose={() => setEditing(false)}
          onSaved={(updated) => { setEditing(false); setQr(updated); loadImage(); }}
        />
      )}
    </div>
  );
}
